/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  refundService,
  type GetRefundsParams,
  type RefundItemRecord,
  type RefundRecord,
} from "@src/@api-services/refund";
import { SortOrder } from "@src/shared/constants/sort-order.constants";
import type { PaginatedListResponse } from "@src/shared/types/pagination.types";
import type { RefundRequest } from "@src/views/apps/financial/types";
import { TRANSACTION_ITEM_TYPE } from "@src/views/apps/financial/transactions/constants";

interface RefundsState {
  data: RefundRequest[];
  total: number;
  params: GetRefundsParams;
  selectedRefund: RefundRequest | null;
  loading: boolean;
  detailLoading: boolean;
  error: string | null;
}

const formatRefundType = (type?: string | null) => {
  if (!type) return "";
  return type
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const mapRequester = (row: RefundRecord) => {
  if (row.requesterName) {
    return {
      name: row.requesterName,
      email: row.requesterEmail ?? undefined,
    };
  }

  if (row.requestedBy) {
    const name =
      `${row.requestedBy.firstName || ""} ${row.requestedBy.lastName || ""}`.trim();
    return {
      name: name || row.requestedBy.email || null,
      email: row.requestedBy.email ?? undefined,
    };
  }

  return { name: null, email: undefined };
};

const mapRefundItem = (item?: RefundItemRecord) => {
  if (!item) return undefined;
  return {
    name: item.name ?? null,
    quantity: Number(item.quantity ?? 0),
    price: Number(item.price ?? 0),
    discountAmount: item.discountAmount,
    totalAmount: Number(item.totalAmount ?? 0),
    itemType: item.itemType ?? null,
    itemId: item.itemId ?? null,
    bookingId: item.bookingId ?? null,
    businessId: item.businessId,
    businessName: item.businessName ?? null,
    transactionId: item.transactionId,
    orderDate: item.orderDate,
  };
};

const mapRefundListItem = (row: RefundRecord): RefundRequest => {
  const requester = mapRequester(row);

  return {
    id: String(row.id),
    transactionId: row.transactionId ? String(row.transactionId) : "",
    itemName: row.itemName ?? undefined,
    requesterName: requester.name,
    requesterEmail: requester.email ?? undefined,
    businessName: row.businessName ?? null,
    refundAmount: Number(row.refundAmount ?? 0),
    cancellationFee: Number(row.cancellationFee ?? 0),
    netRefundAmount: Number(
      row.netRefundAmount ??
        Math.max(Number(row.refundAmount ?? 0) - Number(row.cancellationFee ?? 0), 0)
    ),
    currency: String(row.currency ?? ""),
    reason: row.reason ?? null,
    type: row.type ?? null,
    typeLabel: formatRefundType(row.type),
    status: row.status ?? null,
    createdAt: row.createdAt || new Date().toISOString(),
  };
};

const mapRefundDetail = (row: RefundRecord): RefundRequest => {
  const refundAmount = Number(row.refundAmount ?? 0);
  const cancellationFee = Number(row.cancellationFee ?? 0);
  const itemType = row.item?.itemType;
  const itemId = row.item?.itemId;
  const requester = mapRequester(row);

  return {
    id: String(row.id),
    transactionId: row.transactionId ? String(row.transactionId) : "",
    transactionItemId: row.transactionItemId
      ? String(row.transactionItemId)
      : undefined,
    productReturnId: row.productReturnId ? String(row.productReturnId) : undefined,
    itemName: row.item?.name ?? row.itemName ?? undefined,
    itemType: itemType ?? undefined,
    itemId: itemId != null ? String(itemId) : undefined,
    bookingId:
      itemType?.toUpperCase() === TRANSACTION_ITEM_TYPE.BOOKING && itemId != null
        ? String(itemId)
        : undefined,
    requesterName: requester.name,
    requesterEmail: requester.email ?? undefined,
    businessId: row.businessId ?? row.item?.businessId,
    businessName: row.businessName ?? row.item?.businessName ?? null,
    refundAmount,
    cancellationFee,
    netRefundAmount: Number(
      row.netRefundAmount ?? Math.max(refundAmount - cancellationFee, 0)
    ),
    currency: String(row.currency ?? ""),
    reason: row.reason ?? null,
    type: row.type ?? null,
    typeLabel: formatRefundType(row.type),
    status: row.status ?? null,
    internalNote: row.internalNote ?? null,
    createdAt: row.createdAt || new Date().toISOString(),
    updatedAt: row.updatedAt ?? undefined,
    requestedByUser: row.requestedBy ?? undefined,
    approvedByUser: row.approvedBy ?? undefined,
    processedByUser: row.processedBy ?? undefined,
    item: mapRefundItem(row.item),
  };
};

export const getRefunds = createAsyncThunk(
  "refunds/getRefunds",
  async (params: GetRefundsParams, { rejectWithValue }) => {
    try {
      const response = await refundService.getRefunds(params);
      const payload = response.data?.data as PaginatedListResponse<RefundRecord>;
      const rows = payload?.data ?? [];
      return {
        params,
        data: rows.map(mapRefundListItem),
        total: payload?.totalRecord ?? 0,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch refunds"
      );
    }
  }
);

export const getRefund = createAsyncThunk(
  "refunds/getRefund",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await refundService.getRefund(id);
      const data = response.data?.data as RefundRecord | undefined;
      return data?.id ? mapRefundDetail(data) : null;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch refund"
      );
    }
  }
);

export const retryRefundPayment = createAsyncThunk(
  "refunds/retryRefundPayment",
  async (id: string, { dispatch, getState, rejectWithValue }) => {
    try {
      await refundService.retryRefundPayment(id);
      const state = getState() as { refunds: RefundsState };
      await dispatch(getRefunds(state.refunds.params));
      await dispatch(getRefund(id));
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to retry refund payment"
      );
    }
  }
);

const initialState: RefundsState = {
  data: [],
  total: 0,
  params: {
    page: 1,
    pageSize: 10,
    orderBy: "createdAt",
    orderDirection: SortOrder.DESC,
  },
  selectedRefund: null,
  loading: false,
  detailLoading: false,
  error: null,
};

export const refundsSlice = createSlice({
  name: "refunds",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedRefund: (state) => {
      state.selectedRefund = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getRefunds.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getRefunds.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.total = action.payload.total;
        state.params = action.payload.params;
      })
      .addCase(getRefunds.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.data = [];
        state.total = 0;
      })
      .addCase(getRefund.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(getRefund.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.selectedRefund = action.payload;
      })
      .addCase(getRefund.rejected, (state) => {
        state.detailLoading = false;
        state.selectedRefund = null;
      });
  },
});

export const { clearError, clearSelectedRefund } = refundsSlice.actions;
export default refundsSlice.reducer;
